import React from 'react';

import { css } from '@emotion/css';
import { ExcludeDurationOption, ExcludeDurationOptionLabel } from '@recurrency/core-api-schema/dist/common/enums';
import moment, { Moment } from 'moment/moment';

import { MultiSelectOption } from 'components/AsyncSelect/types';
import { IsoDatePicker } from 'components/DatePicker/IsoDatePicker';
import { FlexSpace } from 'components/FlexSpace';
import { Select } from 'components/Select';

import { truthy } from 'utils/boolean';

import { ISODateStr } from 'types/hash-state';

export function ExcludeDurationSelector({
  overrideClassName,
  selectedDuration,
  setSelectedDuration,
  customDate,
  setCustomDate,
  includeDontExcludeOption = false,
  label = `Exclude for `,
}: {
  overrideClassName?: string;
  selectedDuration: ExcludeDurationOption;
  setSelectedDuration: (_: ExcludeDurationOption) => void;
  customDate?: ISODateStr;
  setCustomDate: (_: ISODateStr | undefined) => void;
  includeDontExcludeOption?: boolean;
  label?: string;
}) {
  const allowedOptions = [
    ExcludeDurationOption.Forever,
    ExcludeDurationOption.OneDay,
    ExcludeDurationOption.OneWeek,
    ExcludeDurationOption.OneMonth,
    ExcludeDurationOption.ThreeMonths,
    ExcludeDurationOption.SixMonths,
    ExcludeDurationOption.Custom,
    includeDontExcludeOption ? ExcludeDurationOption.DontExclude : null,
  ].filter(truthy);

  const excludedUntilOptions: MultiSelectOption[] = allowedOptions.map((value) => ({
    value,
    label: ExcludeDurationOptionLabel[value],
  }));

  const customDateVal = customDate ?? moment().add(1, 'day').toISOString();
  const shouldShowCustomDatePicker = selectedDuration === ExcludeDurationOption.Custom;

  const isFutureDate = (date: Moment) =>
    // Can not select days before tomorrow
    date && date < moment().add(1, 'day').startOf('day');

  return (
    <FlexSpace
      direction="row"
      gap={8}
      alignItems="center"
      className={
        overrideClassName ??
        css`
          margin-top: 10px;
        `
      }
    >
      <span>{label}</span>
      <Select
        className={css`
          width: 140px;
        `}
        size="small"
        value={selectedDuration}
        onSelect={setSelectedDuration}
        options={excludedUntilOptions}
        placeholder="Select duration"
      />
      {shouldShowCustomDatePicker && (
        <IsoDatePicker
          className={css`
            height: 32px;
          `}
          disabledDates={isFutureDate}
          onChange={setCustomDate}
          picker="date"
          value={customDateVal}
          allowClear={false}
          showTime={false}
          size="small"
        />
      )}
    </FlexSpace>
  );
}
